import {
  Breakpoint,
  Breakpoints,
  FeatureFlagProviderName,
  FeatureFlagProvidersMap,
  FeatureFlagQuery,
  ScreenWidthMediaQueryPreset,
  UserPermissions,
  UserRoles,
} from "../components/types";
import { hasItems } from "./array-utils";
import { matchMediaQueryPreset } from "./dom-utils";

export type ShowHideConditionKey<F extends FeatureFlagProviderName> = keyof ShowHideCondition<F>;

export type ShowHideConditionKeysRequiringAuthentication<F extends FeatureFlagProviderName> = keyof Pick<
  ShowHideCondition<F>,
  "permissionsIn" | "rolesIn"
>;

export type ShowHideConditionKeysToEval<F extends FeatureFlagProviderName> = keyof Pick<
  ShowHideCondition<F>,
  "checkAuthentication" | "permissionsIn" | "rolesIn" | "tenantIn" | "screenIs" | "featureFlagIsEnabledForQuery"
>;

export interface ShowHideCondition<F extends FeatureFlagProviderName> {
  /**
   * This prop controls the enforcing of a authentication check.
   *
   * @default false
   */
  checkAuthentication?: boolean;

  /**
   * This prop controls the list of permission user should have to be considered authorized.
   * If passed then `checkAuthentication` is enforced irrespective.
   */
  permissionsIn?: string[];

  /**
   * If `true` then all enforces that all permissions in `permissionsIn` must be be present in `availablePermissions`.
   *
   * @default false
   */
  matchAllPermissions?: boolean;

  /**
   * This prop controls the list of role user should have to be considered authorized.
   * If passed then `checkAuthentication` is enforced irrespective.
   */
  rolesIn?: string[];

  /**
   * If `true` then all enforces that all roles in `rolesIn` must be be present in `availableRoles`.
   *
   * @default false
   */
  matchAllRoles?: boolean;

  /**
   * This prop controls the list of tenant to match to render the component.
   */
  tenantIn?: string[];

  /**
   * This prop can be used to specify a condition on the screen size, either to check if it is `mobile` or `desktop`
   * or to match it against a certain breakpoint.
   */
  screenIs?: ScreenWidthMediaQueryPreset;

  /**
   * This prop can be used to specify a condition for feature flag,
   * This will get the given configuration value from given provider object
   */
  featureFlagIsEnabledForQuery?: FeatureFlagQuery<F>;
}

export type NonUIShowHideCondition<F extends FeatureFlagProviderName> = Omit<ShowHideCondition<F>, "screenIs">;

const conditionKeysRequiringAuthentication: Array<ShowHideConditionKeysRequiringAuthentication<string>> = [
  "permissionsIn",
  "rolesIn",
];

const conditionKeysToEval: Array<ShowHideConditionKeysToEval<string>> = [
  "checkAuthentication",
  "permissionsIn",
  "rolesIn",
  "tenantIn",
  "screenIs",
  "featureFlagIsEnabledForQuery",
];

const requiresPermissionsOrRoleCheck = <F extends FeatureFlagProviderName>(
  showHideConditionKeys: ShowHideConditionKey<F>[]
) => {
  return conditionKeysRequiringAuthentication.some((key: any) => showHideConditionKeys.includes(key));
};

const getConditionKeysToEval = <F extends FeatureFlagProviderName>(
  showHideConditionKeys: ShowHideConditionKey<F>[]
) => {
  return showHideConditionKeys.filter((key: any) =>
    conditionKeysToEval.includes(key)
  ) as ShowHideConditionKeysToEval<F>[];
};

/**
 * This is a utility function to evaluate a set of conditions against a set of currently prevailing or available
 * application level parameters.
 *
 * The function returns result of this evaluation as a single boolean value.
 *
 * This result can be used for conditional rendering logic on the UI.
 *
 * @param isUserAuthenticated A boolean value indicating if the user is currently logged in or not.
 * @param availableUserPermissions A list of permissions granted to the currently logged in user.
 * @param availableUserRoles A list of roles assigned to the currently logged in user.
 * @param activeTenant A string indicating the currently active tenant for the application.
 * @param availableBreakpoints An object containing the available breakpoints as per the currently active theme.
 * @param desktopBreakpoint A string the currently set desktop breakpoint.
 * @param showHideConditions An object representing the conditions to be evaluated against the above set of currently prevailing parameters.
 */
export const evalShowHideConditions = <F extends FeatureFlagProviderName>(
  isUserAuthenticated: boolean,
  availableUserPermissions: UserPermissions,
  availableUserRoles: UserRoles,
  activeTenant: string,
  availableBreakpoints: Breakpoints,
  desktopBreakpoint: Breakpoint,
  featureFlagProvidersMap: FeatureFlagProvidersMap<F>,
  showHideConditions: ShowHideCondition<F>[]
) => {
  const result = showHideConditions.some((showHideCondition) => {
    const conditionKeys = Object.keys(showHideCondition) as ShowHideConditionKey<F>[];
    const conditionKeysToEval = getConditionKeysToEval(conditionKeys);

    let { checkAuthentication = false } = showHideCondition;
    const {
      permissionsIn,
      matchAllPermissions,
      rolesIn,
      matchAllRoles,
      tenantIn,
      screenIs,
      featureFlagIsEnabledForQuery,
    } = showHideCondition;

    if (!checkAuthentication && requiresPermissionsOrRoleCheck(conditionKeysToEval)) {
      conditionKeysToEval.unshift("checkAuthentication");
      checkAuthentication = true;
    }

    return conditionKeysToEval.every((conditionKey) => {
      switch (conditionKey) {
        case "checkAuthentication":
          return isUserAuthenticated;
        case "permissionsIn":
          return hasItems(permissionsIn, availableUserPermissions, matchAllPermissions);
        case "rolesIn":
          return hasItems(rolesIn, availableUserRoles, matchAllRoles);
        case "tenantIn":
          return tenantIn.includes(activeTenant);
        case "screenIs":
          return matchMediaQueryPreset(screenIs, availableBreakpoints, desktopBreakpoint)?.matches;
        case "featureFlagIsEnabledForQuery":
          return evalFeatureFlagQuery(featureFlagProvidersMap, featureFlagIsEnabledForQuery);
        default:
          return false;
      }
    });
  });

  return result;
};

/**
 * A utility function to check if a ShowHideCondition condition object has a `screenIs` predicate.
 * @param condition A ShowHideCondition object to be checked
 */
export const hasScreenIsCondition = <F extends FeatureFlagProviderName>(
  condition: ShowHideCondition<F> = {}
): boolean => {
  return (Object.keys(condition) as ShowHideConditionKey<F>[]).includes("screenIs");
};

/**
 * This is a utility function to evaluate a feature flag query by fetching feature flag configuration from an available
 * set of feature flag providers and comparing that value against a set of acceptable values.
 *
 * The function returns result of this evaluation as a single boolean value.
 *
 * This result can be used for conditional rendering logic on the UI.
 *
 * @param featureFlagProvidersMap a map of feature flag provider names to service provider object instance
 * @param query the feature flag query to be evaluated
 */
export const evalFeatureFlagQuery = <F extends FeatureFlagProviderName>(
  featureFlagProvidersMap: FeatureFlagProvidersMap<F>,
  query: FeatureFlagQuery<F>
): boolean => {
  const { provider, feature, options = {}, acceptableValues } = query;

  const featureFlagProvider = featureFlagProvidersMap?.[provider];
  const featureValue = featureFlagProvider?.getFeatureFlag(feature, options);

  if (acceptableValues?.includes(featureValue)) {
    return true;
  }

  return false;
};
